import { ArrowLeft, ArrowRight } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { RAIL_BAR_CLEARANCE } from "./PriceRail"
import { Stepper } from "./Stepper"

// The wizard frame: stepper rail, a scrollable step card, a sticky price rail,
// and the Back / Save & exit / Continue footer. Presentational only — the page
// owns navigation state and passes everything in.
//
// Props:
//   title, sub        — heading of the current step card
//   steps, activeIndex, completedThrough, onJump  — forwarded to <Stepper>
//   stepLabel         — overrides the "Step N of M" eyebrow (e.g. the entry steps
//                       run "Step 1 of 2" under a 3-node stepper, and the hub
//                       shows the project name). Defaults to "Step N of M".
//   onBack            — Back handler (Back is hidden when backDisabled is true)
//   onSaveExit        — Save & exit handler (subtle link; hidden if absent)
//   onContinue        — primary CTA handler
//   continueLabel     — primary CTA text (default "Continue")
//   continueDisabled  — disables the primary CTA
//   backDisabled      — hides Back (e.g. on the first step)
//   priceRail         — the rendered <PriceRail/> element (right on desktop, a
//                       fixed bottom bar + DS sheet at or below 1080)
//   stepperCollapseAt — per-instance stepper collapse boundary (see <Stepper>).
//                       null (default) = the node rail never collapses.
//   children          — the rendered fields for this step
//   footerNote        — optional small print under the footer row
//
// Responsive (P8-H5 "Footlights"): the two-column split is the design's 1081,
// not Tailwind's `lg` (1024). Below it the frame is one column and the content
// well carries the bar's clearance padding so the Back / Continue row is never
// under the fixed price bar.
export function WizardShell({
  title,
  sub,
  steps = [],
  activeIndex = 0,
  completedThrough = -1,
  stepLabel,
  onBack,
  backLabel = "Back",
  onSaveExit,
  onContinue,
  continueLabel = "Continue",
  continueDisabled = false,
  backDisabled = false,
  onJump,
  priceRail,
  stepperCollapseAt = null,
  children,
  footerNote,
}) {
  const showBack = typeof onBack === "function" && !backDisabled

  return (
    <div className="flex flex-col gap-5">
      <Stepper
        steps={steps}
        activeIndex={activeIndex}
        completedThrough={completedThrough}
        onJump={onJump}
        collapseAt={stepperCollapseAt}
      />

      <div
        className={cn(
          "grid items-start gap-6 min-[1081px]:grid-cols-[minmax(0,1fr)_320px]",
          RAIL_BAR_CLEARANCE
        )}
      >
        <div className="flex min-w-0 flex-col gap-4">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="px-6 pt-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                {stepLabel ?? `Step ${activeIndex + 1} of ${steps.length}`}
              </div>
              {title && (
                <h2 className="mt-1 font-display text-[18px] font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              )}
              {sub && (
                <p className="mt-1 max-w-[580px] text-[13px] leading-relaxed text-muted-foreground text-pretty">
                  {sub}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-5 px-6 pb-6 pt-5">{children}</div>
          </div>

          {/* ≤560 the action row wraps onto a second line (design `.jf-foot` /
              `.sf-foot`); at 365 Back + Save & exit + Continue cannot share one
              line and the primary CTA would otherwise run off-screen. */}
          <div className="flex items-center gap-3 max-[561px]:flex-wrap max-[561px]:gap-y-2.5">
            {showBack && (
              <Button variant="outline" onClick={onBack} className="gap-1.5">
                <ArrowLeft className="size-4" strokeWidth={2} />
                {backLabel}
              </Button>
            )}
            <div className="flex-1" />
            {typeof onSaveExit === "function" && (
              <button
                type="button"
                onClick={onSaveExit}
                className="text-[12.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                Save &amp; exit
              </button>
            )}
            <Button onClick={onContinue} disabled={continueDisabled} className="gap-1.5 font-bold">
              {continueLabel}
              <ArrowRight className="size-4" strokeWidth={2.1} />
            </Button>
          </div>

          {footerNote && (
            <p className={cn("text-[12px] leading-relaxed text-muted-foreground")}>{footerNote}</p>
          )}
        </div>

        <div className="min-[1081px]:sticky min-[1081px]:top-4">{priceRail}</div>
      </div>
    </div>
  )
}
